import React from 'react';

/**
 * Swaps its content with a slide when `swapKey` changes: what was there
 * slides off and fades, the new content slides in after it, both moving the
 * way `direction` says (1 forward, -1 back, 0 swap without moving).
 *
 * The outgoing content is a copy of the DOM taken just before React replaces
 * it - which is why this is a class: getSnapshotBeforeUpdate is the one place
 * the old DOM can still be read. The copy is inert and removed once it has
 * gone.
 */
export default class SlideSwap extends React.Component {
  rootRef = React.createRef();

  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.swapKey === this.props.swapKey || !this.props.direction) return null;
    const current = this.rootRef.current?.querySelector(':scope > .ts-swap__in');
    return current ? current.cloneNode(true) : null;
  }

  componentDidUpdate(prevProps, prevState, ghost) {
    const root = this.rootRef.current;
    if (!ghost || !root) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    ghost.classList.remove('ts-swap__in', 'ts-swap__in--next', 'ts-swap__in--prev');
    ghost.classList.add('ts-swap__ghost', this.props.direction > 0 ? 'ts-swap__out--next' : 'ts-swap__out--prev');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.inert = true;
    root.appendChild(ghost);
    const remove = () => ghost.remove();
    ghost.addEventListener('animationend', remove, { once: true });
    // In case the animation never runs (a background tab, say).
    setTimeout(remove, 800);
  }

  componentWillUnmount() {
    this.rootRef.current?.querySelectorAll(':scope > .ts-swap__ghost').forEach((node) => node.remove());
  }

  render() {
    const {
      as: Tag = 'div',
      innerAs: Inner = 'div',
      swapKey,
      direction = 0,
      className = '',
      children,
      ...rest
    } = this.props;
    const enter = direction > 0 ? ' ts-swap__in--next' : direction < 0 ? ' ts-swap__in--prev' : '';
    return (
      <Tag ref={this.rootRef} className={`ts-swap${className ? ` ${className}` : ''}`} {...rest}>
        <Inner key={swapKey} className={`ts-swap__in${enter}`}>{children}</Inner>
      </Tag>
    );
  }
}
